import _ from 'lodash';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { InboxTaskType } from '@treams/treams-network';
import {
	Card,
	Count,
	Empty,
	Skeleton,
	SquareCheckmarkIcon,
	Subheading,
	tokens,
} from '@treams/treams-ui';

import InboxTask from './InboxTask';

type Props = {
	showEmptyState?: boolean;
	hasCounter?: boolean;
	type?: InboxTaskType;
	title?: string;
};

const SectionHeader = styled(Flex)`
	align-items: center;
	gap: ${tokens.gap.element}px;
	margin-bottom: ${tokens.gap.group}px;
`;

const InboxTasksContainer: FC<Props> = ({ showEmptyState, hasCounter, type, title }) => {
	const { t } = useTranslation();

	const { data, loading, refetch } = graphql.useInboxTasksQuery();

	const inboxTasks = data?.viewer.inboxTasks.slice()?.reverse() ?? [];
	const tasks = type ? inboxTasks.filter((task) => task.type === type) : inboxTasks;

	useEffect(() => {
		const tasksNotInStorage = tasks.filter((task) => !_.some(storedTasks, { id: task.id }));
		const isPending = type && type === InboxTaskType.FeedbackRequestPending;
		const hasUnstoredTasks =
			(tasks.length > 0 && localStorage.getItem('inboxTasks') === null) ||
			tasksNotInStorage.length > 0;

		if (isPending && hasUnstoredTasks) {
			localStorage.setItem('inboxTasks', JSON.stringify(tasks));
		}
	}, [tasks]);

	const storedTasks = JSON.parse(localStorage.getItem('inboxTasks') ?? '[]');
	const displayTasks = type === InboxTaskType.FeedbackRequestPending ? storedTasks : tasks;
	const finishedTask = storedTasks.filter((item) => !_.some(tasks, { id: item.id }))[0];

	return (
		<>
			{(showEmptyState || (showEmptyState && loading) || tasks.length > 0) && (
				<Box>
					<SectionHeader>
						<Subheading>{title ?? t('inbox.header.tasks')}</Subheading>
						{tasks && tasks.length > 0 && hasCounter && <Count count={tasks.length} />}
					</SectionHeader>
					{loading && (
						<Card p={0}>
							<Skeleton.Repeat count={3}>
								<Box p={3}>
									<Skeleton.Box height={20} width="60%" mb={2} />
									<Skeleton.Box height={16} width="40%" />
								</Box>
							</Skeleton.Repeat>
						</Card>
					)}
					{tasks.length === 0 && !loading && (
						<Empty.Card
							icon={<SquareCheckmarkIcon />}
							title={t('inbox.tasks.empty.title')}
							description={t('inbox.tasks.empty.description')}
						/>
					)}
					{tasks.length > 0 && (
						<Card p={0}>
							{displayTasks.map((task) => (
								<InboxTask
									key={task.id}
									task={task as graphql.InboxTask}
									tasks={tasks as graphql.InboxTask[]}
									finishedTask={finishedTask}
									type={type}
									refresh={refetch}
								/>
							))}
						</Card>
					)}
				</Box>
			)}
		</>
	);
};

export default InboxTasksContainer;
